import { useLazyQuery } from '@apollo/client';

import { Arrayable } from '../../../helper/array';
import { UserLanguage } from '../../users/enums/UserLanguage';
import useGetCurrentUser from '../../users/services/useGetCurrentUser';
import MasterDataCountry, {
  MasterDataCountryFilterInput,
  MasterDataCountryOrderByInput,
} from '../models/MasterDataCountry';
import { GET_MASTER_DATA_COUNTRIES } from './query';

interface QueryData {
  masterDataCountries: MasterDataCountry[];
}

interface QueryVariables {
  orderBy?: Arrayable<MasterDataCountryOrderByInput>;
  where?: MasterDataCountryFilterInput;
}

/**
 * Sets the country name to the country object.
 * Takes arguments twice to allow usage as a mapping function.
 */
export const setCountryName =
  (language: UserLanguage) =>
  (country: MasterDataCountry): MasterDataCountry => {
    return {
      ...country,
      name: country?.translations.find((t) => t.code == language)?.name ?? '',
    };
  };

function useFetchCountries() {
  const [execute, { loading, error, data }] = useLazyQuery<QueryData, QueryVariables>(GET_MASTER_DATA_COUNTRIES, {
    notifyOnNetworkStatusChange: true,
  });

  const { language: currentLanguage } = useGetCurrentUser();
  const setLocaleCountryName = setCountryName(currentLanguage);

  const fetchCountries = async () => {
    const response = await execute();

    return response.data?.masterDataCountries.map(setLocaleCountryName) || [];
  };

  return { fetchCountries, loading, error, countries: data?.masterDataCountries.map(setLocaleCountryName) || [] };
}

export default useFetchCountries;
